/**
 * AuthProvider - Bootstrap component that blocks RouterProvider mounting
 * until auth + permissions are fully loaded.
 *
 * Flow:
 * 1. Wait for Zustand rehydration (isHydrated)
 * 2. If authenticated (token exists), fetch permissions from API
 * 3. Populate usePermissionStore with fetched data
 * 4. Set isLoading = false → children render
 */

import { FullScreenLoader } from "@components/common/FullScreenLoader";
import { useAuthStore } from "@lib/auth/authStore";
import { normalizePermissionContext } from "@lib/permissions/parser";
import type { PermissionsSummary } from "@lib/permissions/summary.types";
import { type PermissionQueryResult, permissionQueryOptions } from "@lib/queries/permissionQueries";
import { usePermissionStore } from "@lib/stores/permissions.store";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  // Wait for Zustand auth store rehydration from localStorage
  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });
    // In case it's already hydrated (e.g. strict mode double-fire)
    if (useAuthStore.persist.hasHydrated()) {
      setIsHydrated(true);
    }
    return unsub;
  }, []);

  // Read auth state (only meaningful after hydration)
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.userId);
  const isAuthenticated = isHydrated && !!token && !!userId;

  // Fetch permissions from API when authenticated
  const {
    data: permissionsData,
    isLoading,
    error,
  } = useQuery({
    ...permissionQueryOptions(userId ?? 0, token ?? ""),
    enabled: isAuthenticated,
  }) as { data: PermissionQueryResult | undefined; isLoading: boolean; error: Error | null };

  // Populate permission store when data arrives
  useEffect(() => {
    if (!permissionsData || !isAuthenticated) return;

    const { permissionsArray, context, hasWildcard, summary } = permissionsData;
    const normalizedContext = normalizePermissionContext(
      (context ?? {}) as Record<string, unknown>,
    );

    usePermissionStore.getState().setPermissions({
      permissions: (permissionsArray ?? []) as readonly string[],
      hasWildcard: hasWildcard ?? permissionsArray?.includes("*") ?? false,
      context: normalizedContext,
      summary: (summary as PermissionsSummary) ?? null,
    });
  }, [permissionsData, isAuthenticated]);

  // Error: logout and redirect to login
  useEffect(() => {
    if (error) {
      console.error("[AuthProvider] Failed to load permissions:", error);
      // Not awaited: the redirect below unloads the page and would cancel the
      // revocation request anyway. Nothing is lost by skipping it — permissions
      // could not be read, so there is no live session worth revoking remotely.
      void useAuthStore.getState().logout();
      window.location.href = "/auth/login";
    }
  }, [error]);

  // Block rendering while loading
  if (!isHydrated || (isAuthenticated && isLoading)) {
    return <FullScreenLoader message="Chargement des permissions..." />;
  }

  return <>{children}</>;
}
